import React, { useState } from "react";
import { Crosshair, Loader2, AlertTriangle, Zap, Check } from "lucide-react";
import { roleFit } from "@/lib/api";

export default function Readiness() {
  const [posting, setPosting] = useState("");
  const [resume, setResume] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (posting.trim().length < 50) {
      setError("Paste the full job posting so the score has something to work with.");
      return;
    }
    setError("");
    setLoading(true);
    setResult(null);
    try {
      const res = await roleFit({ posting, resume });
      const score = res?.score ?? res?.readiness ?? 0;
      setResult({
        score: Math.max(0, Math.min(100, Number(score))),
        strengths: res?.strengths || res?.have || [],
        gaps: res?.gaps || res?.missing || [],
        plan: res?.plan || res?.study_plan || res?.next || [],
        summary: res?.summary || res?.message || "",
      });
    } catch (e) {
      setError("Couldn't score it right now — try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="dash-readiness" className="space-y-6">
      <header>
        <div className="font-accent text-xs bg-[#E10600] text-white inline-block px-2.5 py-1 mb-3">
          JOB READINESS · ALL-ACCESS
        </div>
        <h1 className="font-display text-3xl md:text-4xl leading-[0.95]">
          Paste a posting.{" "}
          <span className="bg-[#FFC400] px-2">Find out if you&apos;d survive it.</span>
        </h1>
        <p className="font-mono-body text-sm mt-3 text-[#0a0a0a]/75 max-w-2xl">
          We test your resume against the role, return a 0-100 readiness score, the skill gaps that matter, and a ranked study plan to close them.
        </p>
      </header>

      <form
        onSubmit={submit}
        data-testid="readiness-form"
        className="bg-white border-4 border-[#0a0a0a] shadow-brutal p-5 md:p-6 space-y-4"
      >
        <div>
          <label className="font-accent text-xs block mb-2">JOB POSTING</label>
          <textarea
            value={posting}
            onChange={(e) => setPosting(e.target.value)}
            placeholder="Paste the full job posting (title, requirements, responsibilities)…"
            data-testid="readiness-posting"
            rows={8}
            className="w-full bg-[#FDFBF7] border-2 border-[#0a0a0a] px-3 py-2.5 font-mono-body text-sm outline-none focus:bg-white resize-y"
          />
        </div>
        <div>
          <label className="font-accent text-xs block mb-2">
            YOUR RESUME (optional but more accurate)
          </label>
          <textarea
            value={resume}
            onChange={(e) => setResume(e.target.value)}
            placeholder="Paste your resume text…"
            data-testid="readiness-resume"
            rows={6}
            className="w-full bg-[#FDFBF7] border-2 border-[#0a0a0a] px-3 py-2.5 font-mono-body text-sm outline-none focus:bg-white resize-y"
          />
        </div>
        {error && (
          <div
            data-testid="readiness-error"
            className="flex items-start gap-2 font-mono-body text-xs text-[#E10600] bg-[#E10600]/10 border-2 border-[#E10600] px-3 py-2"
          >
            <AlertTriangle size={14} strokeWidth={2.5} className="mt-0.5" />
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="readiness-submit"
          className="hover-pop inline-flex items-center gap-2 bg-[#E10600] text-white border-4 border-[#0a0a0a] px-5 py-3 font-accent text-base shadow-brutal disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Scoring…
            </>
          ) : (
            <>
              <Crosshair size={14} strokeWidth={3} /> Score my readiness
            </>
          )}
        </button>
      </form>

      {result && (
        <section
          data-testid="readiness-results"
          className="grid grid-cols-1 lg:grid-cols-12 gap-5"
        >
          <div className="lg:col-span-4 bg-[#0a0a0a] text-[#FDFBF7] border-4 border-[#0a0a0a] shadow-brutal p-6 md:p-7">
            <div className="font-accent text-xs text-[#FFC400] mb-3">READINESS</div>
            <div className="flex items-baseline gap-2">
              <span className="font-display text-7xl md:text-8xl text-[#FFC400] leading-none">
                {result.score}
              </span>
              <span className="font-mono-body text-base text-white/60">/ 100</span>
            </div>
            <div className="h-3 bg-[#FDFBF7]/15 border-2 border-[#FFC400] mt-5">
              <div className="h-full bg-[#FFC400]" style={{ width: `${result.score}%` }} />
            </div>
            {result.summary && (
              <p className="font-mono-body text-sm mt-5 text-white/85 leading-relaxed">
                {result.summary}
              </p>
            )}
          </div>

          <div className="lg:col-span-4 bg-white border-4 border-[#0a0a0a] shadow-brutal p-6">
            <div className="font-accent text-xs bg-[#00FF66] inline-block px-2 py-1 mb-3">
              STRENGTHS
            </div>
            <ul className="space-y-2 font-mono-body text-sm">
              {(result.strengths.length ? result.strengths : ["No strengths returned."]).map(
                (s, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check size={14} strokeWidth={3} className="mt-0.5 text-[#E10600]" />
                    <span>{typeof s === "string" ? s : s.label || s.skill || ""}</span>
                  </li>
                )
              )}
            </ul>
          </div>

          <div className="lg:col-span-4 bg-[#FFC400] border-4 border-[#0a0a0a] shadow-brutal p-6">
            <div className="font-accent text-xs bg-[#0a0a0a] text-[#FFC400] inline-block px-2 py-1 mb-3">
              GAPS & STUDY PLAN
            </div>
            <ul className="space-y-2 font-mono-body text-sm">
              {(result.gaps.length ? result.gaps : ["No gap data returned."]).map((g, i) => (
                <li key={`gap-${i}`} className="flex items-start gap-2">
                  <Zap size={14} strokeWidth={3} className="mt-0.5 text-[#E10600]" />
                  <span>{typeof g === "string" ? g : g.label || g.skill || ""}</span>
                </li>
              ))}
              {result.plan?.length > 0 && (
                <li className="pt-2 mt-2 border-t-2 border-dashed border-[#0a0a0a]/30">
                  <div className="font-accent text-xs mb-2">NEXT STEPS</div>
                  <ol className="list-decimal pl-5 space-y-1">
                    {result.plan.map((p, i) => (
                      <li key={`p-${i}`}>{typeof p === "string" ? p : p.text || p.label}</li>
                    ))}
                  </ol>
                </li>
              )}
            </ul>
          </div>
        </section>
      )}
    </div>
  );
}
